/**
 * Meeting Calendar View
 * Calendar-style view of meetings with sync capabilities
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus,
  Video, Building2, Users, Clock, FileText, Mic,
  RefreshCw, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingCalendar = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week
  const [refreshing, setRefreshing] = useState(false);

  const fetchEvents = async () => {
    try {
      setRefreshing(true);
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
      
      const res = await axios.get(
        `${API}/api/meeting-intelligence/calendar/events?token=${token}&start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`
      );
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [token, currentDate]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatMonth = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'teams': return <Video className="h-3 w-3" />;
      case 'hybrid': return <Users className="h-3 w-3" />;
      default: return <Building2 className="h-3 w-3" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-amber-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-blue-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="p-6 space-y-6" data-testid="meeting-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="h-7 w-7 text-blue-600" />
            Meeting Calendar
          </h1>
          <p className="text-slate-500">View and manage your meetings</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchEvents} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => navigate('/meeting-intelligence/meetings/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Meeting
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="text-xl font-semibold min-w-[200px] text-center">
                {formatMonth(currentDate)}
              </h2>
              <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Select value={view} onValueChange={setView}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Week day headers */}
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-t-lg overflow-hidden">
            {weekDays.map(day => (
              <div key={day} className="bg-slate-50 py-2 text-center text-sm font-medium text-slate-600">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-b-lg overflow-hidden">
            {days.map((day, idx) => {
              const dayEvents = getEventsForDay(day.date);
              const isCurrentDay = isToday(day.date);
              
              return (
                <div 
                  key={idx}
                  className={`min-h-[120px] bg-white p-2 ${
                    !day.isCurrentMonth ? 'bg-slate-50' : ''
                  } ${isCurrentDay ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    !day.isCurrentMonth ? 'text-slate-400' : ''
                  } ${isCurrentDay ? 'text-blue-600' : ''}`}>
                    {day.date.getDate()}
                  </div>
                  
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event, eventIdx) => (
                      <button
                        key={eventIdx}
                        onClick={() => navigate(`/meeting-intelligence/meetings/${event.id}`)}
                        className={`w-full text-left p-1 rounded text-xs truncate text-white ${getStatusColor(event.status)} hover:opacity-90 transition-opacity`}
                        title={event.title}
                      >
                        <div className="flex items-center gap-1">
                          {getMeetingTypeIcon(event.meeting_type)}
                          <span className="truncate">{event.title}</span>
                        </div>
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-slate-500 text-center">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500" />
          <span>Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500" />
          <span>In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500" />
          <span>Cancelled</span>
        </div>
      </div>

      {/* Upcoming Events List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upcoming This Week</CardTitle>
        </CardHeader>
        <CardContent>
          {events.filter(e => {
            const eventDate = new Date(e.start);
            const now = new Date();
            const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            return eventDate >= now && eventDate <= weekFromNow && e.status !== 'cancelled';
          }).length > 0 ? (
            <div className="space-y-3">
              {events
                .filter(e => {
                  const eventDate = new Date(e.start);
                  const now = new Date();
                  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                  return eventDate >= now && eventDate <= weekFromNow && e.status !== 'cancelled';
                })
                .sort((a, b) => new Date(a.start) - new Date(b.start))
                .slice(0, 5)
                .map((event, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/meeting-intelligence/meetings/${event.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        event.meeting_type === 'teams' ? 'bg-purple-100 text-purple-600' :
                        event.meeting_type === 'hybrid' ? 'bg-amber-100 text-amber-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {getMeetingTypeIcon(event.meeting_type)}
                      </div>
                      <div>
                        <p className="font-medium">{event.title}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          {new Date(event.start).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {event.location && (
                            <>
                              <span>•</span>
                              {event.location}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {event.has_recording && (
                        <Badge variant="outline" className="text-xs">
                          <Mic className="h-3 w-3 mr-1" /> Recorded
                        </Badge>
                      )}
                      {event.has_mom && (
                        <Badge variant="outline" className="text-xs">
                          <FileText className="h-3 w-3 mr-1" /> MoM
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {event.attendees_count}
                      </Badge>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No upcoming meetings this week</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MeetingCalendar;
